import { useState, useEffect, useCallback } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface FeedbackButtonsProps {
  messageId: string;
  currentFeedback: "up" | "down" | null;
  onFeedback: (messageId: string, feedback: "up" | "down" | null) => void;
}

export function FeedbackButtons({
  messageId,
  currentFeedback,
  onFeedback,
}: FeedbackButtonsProps) {
  const [showThanks, setShowThanks] = useState(false);

  const handleFeedback = useCallback(
    (value: "up" | "down") => {
      const newValue = currentFeedback === value ? null : value;
      onFeedback(messageId, newValue);
      if (newValue !== null) {
        setShowThanks(true);
      }
    },
    [messageId, currentFeedback, onFeedback],
  );

  useEffect(() => {
    if (!showThanks) return;
    const timer = setTimeout(() => setShowThanks(false), 2000);
    return () => clearTimeout(timer);
  }, [showThanks]);

  return (
    <div className="mt-1.5 flex items-center gap-1">
      <button
        type="button"
        onClick={() => handleFeedback("up")}
        className={cn(
          "inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-background/50 sm:h-7 sm:w-7",
          currentFeedback === "up"
            ? "text-green-600"
            : "text-muted-foreground/50 hover:text-muted-foreground",
        )}
        aria-label="Resposta util"
        aria-pressed={currentFeedback === "up"}
      >
        <ThumbsUp className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => handleFeedback("down")}
        className={cn(
          "inline-flex h-11 w-11 items-center justify-center rounded transition-colors hover:bg-background/50 sm:h-7 sm:w-7",
          currentFeedback === "down"
            ? "text-red-600"
            : "text-muted-foreground/50 hover:text-muted-foreground",
        )}
        aria-label="Resposta nao util"
        aria-pressed={currentFeedback === "down"}
      >
        <ThumbsDown className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
      </button>
      {showThanks && (
        <span className="animate-in fade-in duration-300 text-[10px] text-muted-foreground" role="status" aria-live="polite">
          Obrigado!
        </span>
      )}
    </div>
  );
}
